import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  Plus, Mail, Phone, Building, MoreVertical, 
  Archive, Trash2, Edit, RotateCcw, AlertTriangle, Users
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsPage = () => {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [archivedClients, setArchivedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activeRes, archivedRes] = await Promise.all([
        axios.get(`${API}/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/clients/archived`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      setClients(activeRes.data);
      setArchivedClients(archivedRes.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client updated successfully');
      } else {
        await axios.post(`${API}/clients`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client created successfully');
      }
      setShowModal(false);
      setEditingClient(null);
      setFormData({ name: '', email: '', phone: '', company: '', status: 'active' });
      fetchData();
    } catch (error) {
      console.error('Failed to save client:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save client';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (e, client) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      phone: client.phone || '',
      company: client.company || '',
      status: client.status
    });
    setShowModal(true);
  };

  const handleArchive = async (e, clientId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Archive this client?')) return;
    
    setActionLoading(clientId);
    try {
      await axios.post(`${API}/clients/${clientId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to archive client:', error);
      alert('Failed to archive client.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (e, clientId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this client?')) return;
    
    setActionLoading(clientId);
    try {
      await axios.post(`${API}/clients/${clientId}/soft-delete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to delete client:', error);
      alert('Failed to delete client.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (clientId) => {
    if (!window.confirm('Restore this client?')) return;
    
    setActionLoading(clientId);
    try {
      await axios.post(`${API}/clients/${clientId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to restore client:', error);
      alert('Failed to restore client.');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePermanentDelete = async (clientId) => {
    if (!window.confirm('PERMANENTLY DELETE this client? This cannot be undone!')) return;
    if (!window.confirm('Are you absolutely sure?')) return;
    
    setActionLoading(clientId);
    try {
      await axios.delete(`${API}/clients/${clientId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to permanently delete client:', error);
      alert('Failed to delete client.');
    } finally {
      setActionLoading(null);
    }
  };

  const archivedOnly = archivedClients.filter(c => c.is_archived && !c.is_deleted);
  const deletedOnly = archivedClients.filter(c => c.is_deleted);

  const ClientCard = ({ client, showActions = true, isArchived = false }) => (
    <Card className="shadow-lg rounded-xl border-0 card-hover" data-testid={`client-card-${client.id}`}>
      <CardHeader className="pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/dashboard/clients/${client.id}`} className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-xl font-bold text-carbon-black hover:text-fearless-blue transition-colors truncate">
              {client.name}
            </CardTitle>
          </Link>
          {showActions && !isArchived && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0 flex-shrink-0"
                  onClick={(e) => e.preventDefault()}
                  disabled={actionLoading === client.id}
                  data-testid={`client-menu-${client.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  onClick={(e) => handleEdit(e, client)}
                  className="cursor-pointer"
                  data-testid={`edit-client-${client.id}`}
                >
                  <Edit className="h-4 w-4 mr-2 text-blue-600" />
                  <span>Edit</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => handleArchive(e, client.id)}
                  className="cursor-pointer"
                  data-testid={`archive-client-${client.id}`}
                >
                  <Archive className="h-4 w-4 mr-2 text-amber-600" />
                  <span>Archive</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={(e) => handleDelete(e, client.id)}
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  data-testid={`delete-client-${client.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  <span>Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
        <div className="space-y-2 sm:space-y-3">
          <div className="flex items-center text-slate-gray text-xs sm:text-sm">
            <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
            <span className="truncate">{client.email}</span>
          </div>
          {client.phone && (
            <div className="flex items-center text-slate-gray text-xs sm:text-sm">
              <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
              <span className="truncate">{client.phone}</span>
            </div>
          )}
          {client.company && (
            <div className="flex items-center text-slate-gray text-xs sm:text-sm">
              <Building className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
              <span className="truncate">{client.company}</span>
            </div>
          )}
          <div className="pt-2 flex items-center justify-between">
            <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold ${
              client.status === 'active' ? 'status-active' : 'status-inactive'
            }`}>
              {client.status}
            </span>
            <span className="text-xs text-slate-gray">{client.project_count || 0} projects</span>
          </div>
          {client.archived_at && (
            <p className="text-xs text-slate-gray">
              Archived: {new Date(client.archived_at).toLocaleDateString()}
            </p>
          )}
          {client.deleted_at && (
            <p className="text-xs text-red-500">
              Deleted: {new Date(client.deleted_at).toLocaleDateString()}
            </p>
          )}
          {isArchived && (
            <div className="flex space-x-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRestore(client.id)}
                disabled={actionLoading === client.id}
                className="border-green-300 text-green-600 hover:bg-green-50 flex-1 text-xs sm:text-sm"
                data-testid={`restore-client-${client.id}`}
              >
                <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                Restore
              </Button>
              {client.is_deleted && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePermanentDelete(client.id)}
                  disabled={actionLoading === client.id}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid={`permanent-delete-client-${client.id}`}
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="clients-loading">
          <div className="text-xl text-slate-gray">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-carbon-black mb-1 sm:mb-2" data-testid="clients-title">Clients</h1>
            <p className="text-sm sm:text-base text-slate-gray">Manage your client relationships</p>
          </div>
          <Dialog open={showModal} onOpenChange={(open) => {
            setShowModal(open);
            if (!open) {
              setEditingClient(null);
              setFormData({ name: '', email: '', phone: '', company: '', status: 'active' });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="gradient-fearless text-white w-full sm:w-auto" data-testid="add-client-btn">
                <Plus className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-md" data-testid="add-client-modal">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl">{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="client-form">
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue text-sm sm:text-base"
                    required
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue text-sm sm:text-base"
                    required
                    data-testid="client-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue text-sm sm:text-base"
                    data-testid="client-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue text-sm sm:text-base"
                    data-testid="client-company-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue text-sm sm:text-base"
                    data-testid="client-status-input"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <Button type="submit" className="w-full gradient-fearless text-white" data-testid="client-submit-btn">
                  {editingClient ? 'Update Client' : 'Create Client'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="active" className="w-full">
          {/* Mobile-friendly scrollable tabs */}
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="grid w-full grid-cols-3 min-w-[320px] sm:min-w-0">
              <TabsTrigger value="active" className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2 sm:px-4" data-testid="tab-active">
                <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="truncate">Active ({clients.length})</span>
              </TabsTrigger>
              <TabsTrigger value="archived" className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2 sm:px-4" data-testid="tab-archived">
                <Archive className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="truncate">Archived ({archivedOnly.length})</span>
              </TabsTrigger>
              <TabsTrigger value="trash" className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2 sm:px-4" data-testid="tab-trash">
                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="truncate">Trash ({deletedOnly.length})</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="active" className="mt-4 sm:mt-6">
            {clients.length === 0 ? (
              <Card className="shadow-lg rounded-xl border-0">
                <CardContent className="p-8 sm:p-12 text-center">
                  <Users className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-slate-gray opacity-50" />
                  <p className="text-slate-gray text-sm sm:text-lg">No active clients yet</p>
                  <p className="text-xs sm:text-sm text-slate-gray mt-2">Add your first client to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {clients.map((client) => (
                  <ClientCard key={client.id} client={client} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="archived" className="mt-4 sm:mt-6">
            {archivedOnly.length === 0 ? (
              <Card className="shadow-lg rounded-xl border-0">
                <CardContent className="p-8 sm:p-12 text-center">
                  <Archive className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-slate-gray opacity-50" />
                  <p className="text-slate-gray text-sm sm:text-lg">No archived clients</p>
                  <p className="text-xs sm:text-sm text-slate-gray mt-2">Archived clients appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {archivedOnly.map((client) => (
                  <ClientCard key={client.id} client={client} showActions={false} isArchived={true} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="trash" className="mt-4 sm:mt-6">
            <Card className="border-yellow-300 bg-yellow-50 shadow-lg rounded-xl mb-4 sm:mb-6">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start sm:items-center space-x-2 sm:space-x-3">
                  <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                  <p className="text-xs sm:text-sm text-yellow-800">
                    <strong>Warning:</strong> Permanent deletion removes all contact history.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {deletedOnly.length === 0 ? (
              <Card className="shadow-lg rounded-xl border-0">
                <CardContent className="p-8 sm:p-12 text-center">
                  <Trash2 className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-slate-gray opacity-50" />
                  <p className="text-slate-gray text-sm sm:text-lg">Trash is empty</p>
                  <p className="text-xs sm:text-sm text-slate-gray mt-2">Deleted clients appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {deletedOnly.map((client) => (
                  <ClientCard key={client.id} client={client} showActions={false} isArchived={true} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ClientsPage;
